import React, { useState, useEffect } from "react";
import { Icon, useToast } from "@chakra-ui/react";
import { FiEdit3 } from "react-icons/fi";
import { FiTrash2 } from "react-icons/fi";
import { MdCancel } from "react-icons/md";
import { BsCheckCircle } from "react-icons/bs";
import ResponsiveRow from "../../Common/Table/ResponsiveRow";
import TdType from "../../Common/Table/TdType";

const ProductItem = ({
  product,
  products,
  userProductList,
  editProduct,
  initialAction = "row",
  productOptions,
  orderProducts,
  editable = true,
  cancelNewProduct = () => {},
  setIsEdited = () => {},
}) => {
  const [action, setAction] = useState(initialAction);
  const [newProduct, setNewProduct] = useState(product);
  const [invalidInput, setInvalidInput] = useState({
    qty: false,
    price: false,
    discount: false,
  });
  const toast = useToast();

  useEffect(() => {
    setNewProduct(product);
  }, [action]);

  const validateForm = () => {
    if (
      !newProduct.id ||
      !newProduct.qty ||
      !newProduct.price ||
      newProduct.discount === null ||
      invalidInput.qty ||
      invalidInput.price ||
      invalidInput.discount
    ) {
      toast({
        title: "Se ha producido un error.",
        description: "Verifique todos los campos",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } else {
      let product = { ...newProduct };
      product.id = products.find((p) => newProduct.id == p.id).id;
      setIsEdited(true);
      editProduct(product);
      setAction("row");
    }
  };

  const calculateTotal = (product) => {
    let total = (
      product.qty *
      product.price *
      0.01 *
      (100 - product.discount)
    ).toFixed(2);
    if (isNaN(total)) return "-";
    return total;
  };

  const handleCancel = () => {
    setAction("row");
  };

  const handleChange = (val) => {
    setNewProduct({ ...newProduct, [val.target.id]: val.target.value });
  };

  const handleProductChange = (val) => {
    let productInList = orderProducts.find((op) => op.id == val.value);

    if (typeof productInList !== "undefined") {
      toast({
        title: "Error al cargar artículo.",
        description: "El artículo ya se encuentra en el listado.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }
    let productSelected = userProductList.find(
      (product) => val.value == product.id
    );
    setNewProduct({
      ...newProduct,
      name: productSelected.name,
      brand: productSelected.brand,
      id: val.value,
      discount: 0,
      qty: productSelected.bulk,
      price:
        productSelected.cost +
        (productSelected.cost * productSelected.profit) / 100 -
        (productSelected.cost * productSelected.discount) / 100,
    });
  };

  const handleEdit = () => {
    setAction("edit");
  };

  const handleSubmit = () => {
    validateForm();
  };

  const validateQtyInput = () => {
    let error = false;
    if (
      newProduct.id &&
      newProduct.qty %
        parseInt(
          products.find((product) => newProduct.id == product.id).bulk
        ) !=
        0
    )
      error = true;

    return error;
  };

  const validateDiscountInput = () => {
    let error = false;
    if (parseFloat(newProduct.discount) > 5.0) error = true;

    return error;
  };

  const validateInput = (id) => {
    let isInvalid = false;
    let text = "";

    // eslint-disable-next-line default-case
    switch (id) {
      case "qty":
        text = `La cantidad debe ser múltiplo de ${parseInt(
          products.find((product) => newProduct.id == product.id).bulk
        )}`;
        isInvalid = validateQtyInput();
        break;
      case "discount":
        text = `El descuento no puede ser mayor a 5%`;
        isInvalid = validateDiscountInput();
        break;
    }
    if (isInvalid === true) {
      toast({
        title: "Se ha producido un error.",
        description: text,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
    setInvalidInput({ ...invalidInput, [id]: isInvalid });

    return true;
  };

  const stockStyling = () => {
    let productSelected = userProductList.find(
      (userProduct) => userProduct.id == product.id
    );
    //En caso de que no lo encuentre en el listado de userProductList (por haber sido borrado o desactivado)
    if (!productSelected) {
      return { color: "red", as: "b" };
    }
    if (productSelected) {
      if (Number(productSelected.stock) - Number(product.qty) < 0)
        return { color: "red", as: "b" };
      if (productSelected.stock < productSelected.minStock)
        return { color: "orange" };
    }
  };

  const rowDisplay = () => {
    let rowData = [];
    // eslint-disable-next-line default-case
    switch (action) {
      case "edit":
        rowData = [
          {
            tdStyle: { width: "5%" },
            item: (
              <TdType
                inputType="input"
                id="qty"
                value={newProduct.qty}
                isInvalid={invalidInput.qty}
                onEnterPressed={handleSubmit}
                onBlur={(e) => validateInput(e.target.id)}
                onChange={handleChange}
              />
            ),
          },
          {
            tdStyle: { width: "25%" },
            item: <TdType text={product.brand + " " + product.name} />,
          },
          {
            tdStyle: { textAlign: "center" },
            item: (
              <TdType
                inputType="input"
                id="price"
                type="number"
                onEnterPressed={handleSubmit}
                value={newProduct.price}
                onChange={handleChange}
              />
            ),
          },
          {
            tdStyle: { textAlign: "center" },
            item: (
              <TdType
                inputType="input"
                fontSize="1em"
                borderColor="cornflowerblue"
                id="discount"
                type="number"
                isInvalid={invalidInput.discount}
                onEnterPressed={handleSubmit}
                onBlur={(e) => validateInput(e.target.id)}
                value={newProduct.discount}
                onChange={handleChange}
              />
            ),
          },
          {
            tdStyle: { textAlign: "center" },
            item: <TdType text={calculateTotal(newProduct)} />,
          },
          {
            tdStyle: { textAlign: "center" },
            item: (
              <TdType
                text={
                  newProduct.id &&
                  products.find((product) => newProduct.id == product.id).stock
                }
              />
            ),
          },
          {
            tdStyle: { textAlign: "center", fontSize: "1.5em", width: "15%" },
            item: (
              <>
                <Icon
                  _hover={{ cursor: "pointer" }}
                  mx="2"
                  as={BsCheckCircle}
                  onClick={() => handleSubmit()}
                  color="green"
                  title={"Aceptar"}
                ></Icon>
                <Icon
                  _hover={{ cursor: "pointer" }}
                  mx="2"
                  as={MdCancel}
                  color="red"
                  title={"Cancelar"}
                  onClick={() => handleCancel()}
                ></Icon>
              </>
            ),
          },
        ];
        return <ResponsiveRow key={product.id} cells={rowData}></ResponsiveRow>;
      case "new":
        rowData = [
          {
            tdStyle: { width: "5%" },
            item: (
              <TdType
                inputType="input"
                isDisabled={!newProduct.id}
                id="qty"
                value={newProduct.qty}
                onChange={handleChange}
                isInvalid={invalidInput.qty}
                onEnterPressed={handleSubmit}
                onBlur={(e) => validateInput(e.target.id)}
              />
            ),
          },
          {
            tdStyle: { width: "25%" },
            item: (
              <TdType
                inputType="modal"
                name={`name`}
                id={`name`}
                title={"Elija un artículo"}
                modalTitle={"Listado de artículos"}
                placeholder={"Elija un artículo"}
                options={productOptions}
                value={productOptions.find(
                  (item) => item.value == newProduct.id
                )}
                onChange={(val) => handleProductChange(val)}
              />
            ),
          },
          {
            tdStyle: { textAlign: "center" },
            item: (
              <TdType
                inputType="input"
                isDisabled={!newProduct.id}
                id="price"
                type="number"
                onEnterPressed={handleSubmit}
                value={newProduct.price}
                onChange={handleChange}
              />
            ),
          },
          {
            tdStyle: { textAlign: "center" },
            item: (
              <TdType
                id="discount"
                inputType="input"
                type="number"
                isDisabled={!newProduct.id}
                value={newProduct.discount}
                onChange={handleChange}
                onEnterPressed={handleSubmit}
                isInvalid={newProduct.discount > 5.0}
                onBlur={(e) => validateInput(e.target.id)}
              />
            ),
          },
          {
            tdStyle: { textAlign: "center" },
            item: <TdType text={calculateTotal(newProduct)} />,
          },
          {
            tdStyle: { textAlign: "center" },
            item: (
              <TdType
                text={
                  newProduct.id &&
                  products.find((product) => newProduct.id == product.id).stock
                }
              />
            ),
          },
          {
            tdStyle: { textAlign: "center", fontSize: "1.5em", width: "15%" },
            item: (
              <>
                <Icon
                  _hover={{ cursor: "pointer" }}
                  mx="2"
                  as={BsCheckCircle}
                  color="green"
                  onClick={() => handleSubmit()}
                  title={"Aceptar"}
                ></Icon>
                <Icon
                  _hover={{ cursor: "pointer" }}
                  mx="2"
                  as={MdCancel}
                  color="red"
                  title={"Cancelar"}
                  onClick={() => cancelNewProduct()}
                ></Icon>
              </>
            ),
          },
        ];
        return <ResponsiveRow key={product.id} cells={rowData}></ResponsiveRow>;
      case "row":
        rowData = [
          {
            tdStyle: { width: "5%" },
            item: <TdType text={product.qty} />,
          },
          {
            tdStyle: { width: "25%" },
            item: <TdType text={product.brand + " " + product.name} />,
          },
          {
            tdStyle: { textAlign: "center" },
            item: <TdType text={product.price} />,
          },
          {
            tdStyle: { textAlign: "center" },
            item: <TdType text={product.discount} />,
          },
          {
            tdStyle: { textAlign: "center" },
            item: <TdType text={calculateTotal(product)} />,
          },
          {
            tdStyle: { textAlign: "center" },
            item: (
              <TdType
                customStyle={stockStyling()}
                text={
                  newProduct.id &&
                  (products.find((product) => newProduct.id == product.id)
                    ?.stock ??
                    "-")
                }
              />
            ),
          },
          {
            tdStyle: {
              textAlign: "center",
              fontSize: "1.5em",
              width: "15%",
            },
            item: (
              <>
                <Icon
                  mx="2"
                  as={FiEdit3}
                  color={editable ? "inherit" : "grey"}
                  _hover={
                    editable ? { cursor: "pointer" } : { cursor: "not-allowed" }
                  }
                  onClick={editable ? () => handleEdit(product) : null}
                  title={"Editar artículo"}
                ></Icon>
                <Icon
                  mx="2"
                  as={FiTrash2}
                  title={"Eliminar artículo"}
                  color={editable ? "inherit" : "grey"}
                  _hover={
                    editable ? { cursor: "pointer" } : { cursor: "not-allowed" }
                  }
                  onClick={
                    editable ? () => editProduct(product, "delete") : null
                  }
                ></Icon>
              </>
            ),
          },
        ];
        return <ResponsiveRow key={product.id} cells={rowData}></ResponsiveRow>;
    }
  };

  return rowDisplay();
};

export default ProductItem;
